// ──────────────────────────────────────────────────────
//  src/pages/FundReport.jsx   (banks + paymodes dropdown)
// ──────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import toastr from "toastr";
import { authFetch } from "../utils/authFetch";
import { hasPerm } from "../utils/permission";

/* ---------- helpers ---------- */
const toast = (msg, type = "success") => {
  toastr.options = { closeButton: true, progressBar: true, timeOut: "3000" };
  toastr[type](msg);
};


/* ---------- config ---------- */
// const API_V1 = "http://52.55.165.210:6060/api/v1";
// const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };

/* ---------- component ---------- */
function FundReport() {
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  
  const roleId = currentUser.role_id;

// alert("Role ID: " + roleId);

  const emptyForm = {
    user_id: currentUser.id || "",
    fundbank_id: "",
    // ref_no: "",
    paymode: "",
    paydate: "",
    amount: "",
    payment_status: "pending",
    remark: "",
    payslip: null,
  };

  const [form, setForm] = useState(emptyForm);
  const [reports, setReports] = useState([]);
  const [banks, setBanks] = useState([]);
  const [paymodes, setPaymodes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ─── fetch dropdown data ─── */
  const fetchBanksAndPaymodes = async () => {
    try {
      const [bankRes, modeRes] = await Promise.all([
        authFetch(`/api/v1/bank/list`),
        authFetch(`/api/v1/paymode/list`),
      ]);
      const banksJson = await bankRes.json();
      const modesJson = await modeRes.json();
      setBanks(Array.isArray(banksJson) ? banksJson : []);
      setPaymodes(Array.isArray(modesJson) ? modesJson : []);
    } catch {
      toast("Failed to load dropdown data", "error");
    }
  };

  /* ─── fetch reports ─── */
  const fetchReports = async () => {
    try {
      const res = await authFetch(`/api/v1/fundreport/list`);
      const data = await res.json();
      const normal = (Array.isArray(data) ? data : []).map((r) => ({
        ...r,
        id: r.id || r._id,
      }));
      setReports(normal);
    } catch {
      toast("Failed to fetch reports", "error");
    }
  };

  useEffect(() => {
    fetchBanksAndPaymodes();
    fetchReports();
  }, []);

  /* ─── create / update ─── */
  const saveReport = async () => {
    // if (!form.amount || !form.ref_no || !form.fundbank_id || !form.paymode ) {
    if (!form.amount || !form.fundbank_id || !form.paymode ) {
      return toast("Bank, Pay Mode, Amount & Ref No & Status are required", "error");
    }
    setLoading(true);

    const url    = editingId ? `/api/v1/fundreport/update/${editingId}` : `/api/v1/fundreport/create`;
    const method = editingId ? "PUT" : "POST";

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== null && v !== "") fd.append(k, v);
    });
    if (editingId) fd.append("id", editingId);

    try {
      const res  = await authFetch(url, { method,body: fd });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Request failed");

      toast(editingId ? "Report updated" : "Report created", editingId ? "info" : "success");
      setForm(emptyForm);
      setEditingId(null);
      fetchReports();
      document.querySelector("#reportModal .btn-close")?.click();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  /* ─── edit ─── */
  const handleEdit = (r) => {
    setForm({
      user_id: r.user_id,
      fundbank_id: r.fundbank_id,
    //   ref_no: r.ref_no,
      paymode: r.paymode,
      paydate: r.paydate?.slice(0, 10) || "",
      amount: r.amount,
    //   payment_status: r.payment_status,
      remark: r.remark,
      payslip: null,
    });
    setEditingId(r.id);
  };

  /* ─── delete ─── */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this report?")) return;
    try {
      const res  = await authFetch(`/api/v1/fundreport/delete/${id}`, { method: "DELETE" });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Delete failed");
      toast("Report deleted", "error");
      fetchReports();
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const onChange = (e) => {
    const { name, value, files } = e.target;
    setForm({ ...form, [name]: files ? files[0] : value });
  };

  const handleStatusChange = async (id, newStatus) => {
  if (!window.confirm(`Are you sure you want to mark this report as ${newStatus}?`)) return;

  try {
    let url = "";
    let method = "POST";
    let body = null;

    if (newStatus === "approved") {
      // ✅ Approve API (no body)
      url = `/api/v1/fundreport/approve/${id}`;
    } else if (newStatus === "rejected") {
      // ✅ Reject API (reason required)
      url = `/api/v1/fundreport/reject/${id}`;
      body = JSON.stringify({ reason: "Rejected by admin" }); // or dynamic reason
    } else {
      // fallback (optional: normal status change)
      url = `/api/v1/fundreport/update/${id}`;
      method = "PUT";
      body = JSON.stringify({ status: newStatus });
    }

    const res = await authFetch(url, {
      method,
      headers: {
        
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      body,
    });

    const json = await res.json();
    if (!res.ok) throw new Error(json.detail || "Failed");

    toast(
      newStatus === "approved"
        ? "✅ Fund approved successfully"
        : newStatus === "rejected"
        ? "❌ Fund rejected"
        : `Status changed to ${newStatus}`,
      "success"
    );
    fetchReports();
  } catch (err) {
    toast(err.message, "error");
  }
};

  /* ─── UI ─── */
  return (
    <div className="page-wrapper">
      <div className="page-content-wrapper">
        <div className="page-content">
          <div className="card">
            <div className="card-body">
              {/* header */}
              <div className="card-title d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Fund Reports</h4>
                {roleId == "1" ? (
                  // 🧩 Superadmin — kuch mat dikhana (ya jo dikhana ho wo yaha likh)
                  null
                ) : (
                <button className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#reportModal"
                  onClick={() => { setForm(emptyForm); setEditingId(null); }}>
                  Add
                </button>
                )}
              </div>

              {/* modal */}
              <div className="modal fade" id="reportModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog"><div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{editingId ? "Edit" : "Add"} Report</h5>
                    <button type="button" className="btn-close" data-bs-dismiss="modal" />
                  </div>
                  <div className="modal-body">
                    {/* Bank dropdown */}
                    <label className="form-label">Bank</label>
                    <select name="fundbank_id" className="form-control mb-2" value={form.fundbank_id} onChange={onChange}>
                      <option value="">-- select bank --</option>
                      {banks.map((b) => (
                        <option key={b.id || b._id} value={b.id || b._id}>{b.name}</option>
                      ))}
                    </select>

                    {/* Paymode dropdown */}
                    <label className="form-label">Pay Mode</label>
                    <select name="paymode" className="form-control mb-2" value={form.paymode} onChange={onChange}>
                      <option value="">-- select mode --</option>
                      {paymodes.map((m) => (
                        <option key={m.id || m._id} value={m.name}>{m.name}</option>
                      ))}
                    </select>
                    
                    {/* rest of inputs */}
                    {[
                    //   ["ref_no", "Ref No"],
                      ["paydate", "Pay Date", "date"],
                      ["amount", "Amount", "number"],
                    //   ["payment_status", "Payment Status"],
                      ["remark", "Remark"],
                    ].map(([field, label, type]) => (
                      <div key={field}>
                        <label className="form-label">{label}</label>
                        <input
                          type={type || "text"}
                          name={field}
                          className="form-control mb-2"
                          value={form[field] ?? ""}
                          onChange={onChange}
                        />
                      </div>
                    ))}
                    <label className="form-label">Payslip (image/pdf)</label>
                    <input name="payslip" type="file" className="form-control mb-2" onChange={onChange} />
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                    <button className="btn btn-primary" onClick={saveReport} disabled={loading}>
                      {loading ? "Saving…" : editingId ? "Update" : "Save"}
                    </button>
                  </div>
                </div></div>
              </div>

              {/* table */}
              <hr />
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>Bank</th>
                      {/* <th>Ref No</th> */}
                      <th>User Name</th>
                      <th>Amount</th>
                      <th>Pay Mode</th>
                      <th>Status</th>
                      <th>Date</th>
                      {roleId === "1" && (
                      <th>Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {reports.length === 0 ? (
                      <tr><td colSpan="6" className="text-center">No reports</td></tr>
                    ) : reports.map((r) => (
                      <tr key={r.id}>
                        <td>{banks.find(b => (b.id||b._id) === r.fundbank_id)?.name || r.fundbank_id}</td>
                        {/* <td>{r.ref_no}</td> */}
                        <td>{r.user_name}</td>
                        <td>{r.amount}</td>
                        <td>{r.paymode}</td>
                        <td>{r.status}</td>
                        <td>{r.paydate}</td>
                        {roleId === "1" && (
                        <td>
                          <button className="btn btn-sm btn-info me-2 d-none" data-bs-toggle="modal"
                            data-bs-target="#reportModal" onClick={() => handleEdit(r)}>
                            <span className="bx bx-edit" />
                          </button>
                          {/* <button className="btn btn-sm btn-danger" onClick={() => handleDelete(r.id)}>
                            <span className="bx bx-trash" />
                          </button> */}
                          
                          {r.status === "pending" ? (
                            <select
                              className={`form-select form-select-sm ${
                                r.status === "approved"
                                  ? "border-success"
                                  : r.status === "rejected"
                                  ? "border-danger"
                                  : "border-secondary"
                              }`}
                              value={r.status}
                              onChange={(e) => handleStatusChange(r.id, e.target.value)}
                              style={{ width: "120px", display: "inline-block" }}
                            >
                              <option value="pending">Pending</option>
                              <option value="approved">Approved</option>
                              <option value="rejected">Rejected</option>
                            </select>
                          ) : (
                          <span
                            className={`badge rounded-pill ${
                              r.status === "approved"
                                ? "bg-success"
                                : r.status === "rejected"
                                ? "bg-danger"
                                : "bg-secondary"
                            }`}
                          >
                           Admin {r.status}
                          </span>
                        )}

                        </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FundReport;
